/**
 * appErrors: the Admin Errors tab's data (GET /v1/admin-review/errors),
 * the browser's page-crash report (POST /v1/client-errors), and the
 * plain words the tab uses for a failure: which screen, what happened,
 * and what the person saw.
 */
import { ApiError, authExpiredError, authExpiry } from './api';
import { errorText, refClause, type SupportContact } from './errorCopy';
import { EMAIL_FIDELITY_EDIT_ROUTE, labelForRoute } from './aiUsage';

export type ErrorGroupSource = 'server' | 'browser' | 'ai';

export interface ErrorOccurrenceWire {
  reference: string;
  occurred_at: string;
  method: string | null;
  detail: string | null;
  user_display_name: string | null;
}

export interface ErrorGroupWire {
  key: string;
  source: ErrorGroupSource;
  route: string;
  error_code: string;
  status: number | null;
  count: number;
  people: number;
  first_at: string;
  last_at: string;
  latest: ErrorOccurrenceWire;
}

export interface ErrorsReportWire {
  window_days: number;
  groups: ErrorGroupWire[];
  request_id?: string;
}

export const ERROR_WINDOWS = [
  { days: 1, label: '24 hours' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
] as const;
export type ErrorWindowDays = (typeof ERROR_WINDOWS)[number]['days'];

export async function fetchErrorsReport(slug: string, days: ErrorWindowDays, signal?: AbortSignal): Promise<ErrorsReportWire> {
  const path = `/api/t/${encodeURIComponent(slug)}/v1/admin-review/errors?days=${days}`;
  const res = await fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, redirect: 'manual', signal });
  if (res.type === 'opaqueredirect') {
    authExpiry.redirectToLogin();
    throw authExpiredError(`GET ${path}`, true);
  }
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    throw new ApiError(res.status, body, `GET ${path} -> HTTP ${res.status}`);
  }
  return (await res.json()) as ErrorsReportWire;
}

export interface PageCrashReport {
  reference: string;
  route: string;
  error_code: string;
  detail: string | null;
}

/**
 * Tell the office a page crashed. Fire and forget: the crash screen is
 * already up, and nothing about this report may change what it shows.
 * keepalive lets the report finish even if the person reloads at once.
 */
/** A short code the person can quote, minted in the browser. */
export function mintReference(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID().slice(0, 8);
  return Math.random().toString(16).slice(2, 10);
}

export function reportPageCrash(slug: string, report: PageCrashReport): void {
  try {
    void fetch(`/api/t/${encodeURIComponent(slug)}/v1/client-errors`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(report),
      redirect: 'manual',
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* the screen stands regardless */
  }
}

/** The screen a route belongs to, in the words the person would use. */
export function screenFor(route: string): string {
  const r = route.toLowerCase();
  if (r.includes('/ai-tools/')) return labelForRoute(route.replace(/^\/api\/t\/\{tenantslug\}\/v1/i, '/api'));
  if (r.includes('/whoami')) return 'Sign-in';
  if (r.includes('/client-errors')) return 'A page in the browser';
  if (r.includes('/admin-review') || r.includes('/admin/')) return 'Admin';
  if (r.includes('/home')) return 'Home';
  if (r.includes('/notes') || r.includes('/encounter')) return 'Notes';
  if (r.includes('/clients/') && r.includes('/record')) return 'Client record';
  if (r.includes('/clients/{id}')) return 'Client record';
  if (r.includes('/clients')) return 'Clients';
  if (r.includes('/invoices') || r.includes('/billing')) return 'Billing';
  if (r.includes('/work-items') || r.includes('/workspace')) return 'Workspace';
  if (r.includes('/team') || r.includes('/staff') || r.includes('/capacity')) return 'Team';
  if (r.includes('/spice')) return 'SPICE';
  if (r.includes('/email')) return 'Email helper';
  return route;
}

/** What happened, in plain words, from the code the writer recorded. */
export function whatHappened(group: Pick<ErrorGroupWire, 'source' | 'error_code' | 'status'>): string {
  const code = group.error_code;
  if (group.source === 'browser') {
    if (code === 'auth_expired_write') return 'A save met an expired sign-in';
    if (code === 'module_load_failed') return 'A page opened before an update could not load part of Olive';
    if (code === 'print_failed') return 'Print or PDF failed';
    if (code === 'app_refused_value') return 'A save was refused for a value Olive sent, not one the person typed';
    return 'A page crashed in the browser';
  }
  if (group.source === 'ai') {
    switch (code) {
      case 'anthropic_max_tokens':
      case 'max_tokens':
        return "The model's answer was cut short";
      case 'anthropic_invalid_json':
      case 'invalid_body':
      case 'invalid_response':
        return "The model's answer didn't fit the report";
      case 'anthropic_call_failed':
      case 'service_down':
        return 'The model could not be reached';
      case 'rate_limited':
        return 'Too many AI runs at once';
      case 'edited_body_rejected':
        return "The editor's answer was not an edit";
      default:
        return `The AI run failed (${code})`;
    }
  }
  switch (code) {
    case 'internal_error':
      return "Olive hit a fault it didn't expect";
    case 'anthropic_call_failed':
      return 'The model could not be reached';
    default:
      return group.status !== null && group.status >= 500
        ? `Olive answered with a server fault (${code})`
        : `Olive refused the request (${code})`;
  }
}

/** The four answers the person saw for this failure. */
export function theySaw(group: ErrorGroupWire, contact: SupportContact): string {
  if (group.source === 'browser') return errorText('page_crash', 'crashed', contact);
  if (group.source === 'ai') {
    // The fidelity editor fails open: its failure never costs the draft.
    if (group.route === EMAIL_FIDELITY_EDIT_ROUTE) {
      return "The draft came through as the writer wrote it. The fidelity editor's pass failed, so Olive served the draft unedited; nothing they entered was lost.";
    }
    return 'The draft did not come through. What they entered stayed in the form; Olive retried once on its own before saying so.';
  }
  return errorText('page_read', 'server', contact, {
    what: 'this part of the page',
    ref: refClause(group.latest.reference),
  });
}

/** Everything about the group, as text to paste to an engineer. */
export function copyForFable(group: ErrorGroupWire): string {
  return [
    `Olive error ref ${group.latest.reference}`,
    `source: ${group.source}`,
    `route: ${group.route}`,
    `code: ${group.error_code}${group.status !== null ? ` (HTTP ${group.status})` : ''}`,
    `detail: ${group.latest.detail ?? '(none)'}`,
    `latest: ${group.latest.occurred_at} by ${group.latest.user_display_name ?? 'unknown'}${group.latest.method ? ` (${group.latest.method})` : ''}`,
    `occurrences: ${group.count} (${group.people} ${group.people === 1 ? 'person' : 'people'}) between ${group.first_at} and ${group.last_at}`,
  ].join('\n');
}
